import { showNotification } from '@mantine/notifications';
import type { EntityType } from '@repo/common/enums/entityType';
import type { LineageFlowNodeActions } from '@repo/lineage/components/EntityNode/EntityNode.types';
import LineageFlowStore from '@repo/lineage/store/LineageFlowStore';
import LineageFlowUIStore from '@repo/lineage/store/LineageFlowUIStore';
import type { FlowActions } from '@repo/lineage/store/LineageFlowUIStore.types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAuthUser } from '../../api';
import { useBackgroundJob2 } from '../BackgroundJobProgress/BackgroundJob2.hooks';
import entityDrawerStore from '../EntityDrawer/store';
import { FilterOptionType, useFilterStoreWithPersistence } from '../Filter';
import { FILTER_OPTIONS_CONFIG } from '../Filter/constants';
import { LINEAGE_GRAPH_FILTER_KEY } from '../LineageGraph/constants';
import { impactAnalysisStore } from '../LineageGraph/Modals/ImpactAnalysisModal/store';
import { exportToCSV, getLineageErrorMessage } from '../LineageGraph/utils';

export const useLineageTab = (
	id: string,
	published: boolean,
	entityType: EntityType,
	nativeType?: string | null
) => {
	const { isViewerOrGuestUser, isEditorOrAdminUser } = useAuthUser();
	const { searchFilterV2Store: filterStore } = useFilterStoreWithPersistence({
		filterOptions: [
			FILTER_OPTIONS_CONFIG[FilterOptionType.NATIVE_TYPE],
			FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION],
		],
		filterLocalStorageKey: `${LINEAGE_GRAPH_FILTER_KEY}-${nativeType ?? entityType}`,
	});

	const queryClient = useQueryClient();

	const [startJob] = useBackgroundJob2(
		undefined,
		'Export completed successfully',
		true
	);

	const handleExportToCSV = useCallback(() => {
		exportToCSV(id, startJob);
	}, [id, startJob]);

	const flowActions: FlowActions = useMemo(
		() => ({
			exportToCSV: {
				label: 'Export to CSV',
				icon: 'csv',
				onClick: handleExportToCSV,
			},
		}),
		[handleExportToCSV]
	);

	const handleAnalyzeImpact = useCallback((nodeID: string) => {
		impactAnalysisStore.setId(nodeID);
		impactAnalysisStore.setOpened(true);
	}, []);

	const handleNodeClick = useCallback(
		async (nodeID: string) => {
			if (isViewerOrGuestUser && !published) {
				entityDrawerStore.openEntityDrawerWithError();
			} else {
				await entityDrawerStore.openEntityDrawerById(
					isEditorOrAdminUser,
					nodeID
				);
			}
		},
		[isViewerOrGuestUser, published, isEditorOrAdminUser]
	);

	const nodeActions: Partial<LineageFlowNodeActions> = useMemo(
		() => ({
			analyzeImpact: {
				label: 'Analyze impact',
				icon: 'reportSearch',
				onClick: handleAnalyzeImpact,
			},
			nodeClick: {
				label: 'Edit in sidebar',
				icon: 'layoutSidebarRightExpand',
				onClick: handleNodeClick,
			},
		}),
		[handleNodeClick, handleAnalyzeImpact]
	);

	const handleLineageApiError = useCallback((error: string) => {
		showNotification({
			title: 'Error',
			message: getLineageErrorMessage(error),
			color: 'red',
		});
	}, []);

	const [lineageFlowStore] = useState(
		() => new LineageFlowStore(id, queryClient, handleLineageApiError)
	);
	const [lineageFlowUIStore] = useState(
		() => new LineageFlowUIStore(flowActions, nodeActions)
	);

	return {
		lineageFlowStore,
		lineageFlowUIStore,
		lineageFilterStore: filterStore,
	};
};
